import { useCallback, useEffect, useState } from 'react';
import { userAPI } from '../../services/api';
import { responseData, responseEnvelope } from '../../services/apiResponse';

export const OTP_STATES = {
	ENTER_CONTACT: 'enter_contact',
	SENDING_CODE: 'sending_code',
	ENTER_CODE: 'enter_code',
	VERIFYING_CODE: 'verifying_code',
	CONFIRM_REGISTRATION: 'confirm_registration',
	REGISTERING: 'registering',
	AUTHENTICATED: 'authenticated',
};

function errorMessage(error, fallback) {
	return error.response?.data?.detail || error.response?.data?.error_description || fallback;
}

export default function useOtpAuth({ allowedContacts, initialMethod, requestId, linkToken, onAuthenticated }) {
	const methods = Object.keys(allowedContacts);
	const [method, setMethod] = useState(initialMethod || methods[0]);
	const [state, setState] = useState(OTP_STATES.ENTER_CONTACT);
	const [challengeId, setChallengeId] = useState('');
	const [registrationToken, setRegistrationToken] = useState('');
	const [lastIdentifier, setLastIdentifier] = useState('');
	const [maskedDestination, setMaskedDestination] = useState('');
	const [code, setCode] = useState('');
	const [countdown, setCountdown] = useState(0);
	const [error, setError] = useState('');

	useEffect(() => {
		if (countdown <= 0) return undefined;
		const timer = window.setTimeout(() => setCountdown((value) => Math.max(0, value - 1)), 1000);
		return () => window.clearTimeout(timer);
	}, [countdown]);

	const clearProofs = useCallback(() => {
		setChallengeId('');
		setRegistrationToken('');
		setCode('');
	}, []);

	const sendCode = useCallback(async (identifier) => {
		setState(OTP_STATES.SENDING_CODE);
		setError('');
		clearProofs();
		try {
			const contact = allowedContacts[method];
			const response = await userAPI.sendVerificationCode({
				identityType: method,
					identifier: contact.requestBound ? undefined : identifier,
					requestId: contact.requestBound ? requestId : undefined,
					linkToken: contact.requestBound ? linkToken : undefined,
			});
			setChallengeId(responseData(response).challenge_id);
			setLastIdentifier(contact.requestBound ? '' : identifier);
			setMaskedDestination(responseData(response).masked_destination || contact.maskedValue || 'your contact');
			setCountdown(responseData(response).resend_after_seconds ?? 60);
			setState(OTP_STATES.ENTER_CODE);
		} catch (sendError) {
			const retryAfter = Number(sendError.response?.headers?.['retry-after']);
			if (Number.isFinite(retryAfter) && retryAfter > 0) setCountdown(retryAfter);
			setError(errorMessage(sendError, method === 'phone'
				? 'WhatsApp verification is unavailable. Try again or use email.'
				: 'Email verification is unavailable. Try again.'));
			setState(OTP_STATES.ENTER_CONTACT);
		}
	}, [allowedContacts, clearProofs, linkToken, method, requestId]);

	const verify = useCallback(async () => {
		if (!/^\d{6}$/.test(code)) {
			setError('Enter the six-digit verification code.');
			return;
		}
		setState(OTP_STATES.VERIFYING_CODE);
		setError('');
		try {
			const response = await userAPI.login(challengeId, code);
			setState(OTP_STATES.AUTHENTICATED);
			clearProofs();
			try {
				await onAuthenticated(response, { method, identifier: lastIdentifier });
			} catch {
				setError('Authentication succeeded, but this payment request is unavailable.');
				setState(OTP_STATES.ENTER_CONTACT);
			}
		} catch (verifyError) {
			const data = verifyError.response?.data || {};
			const metadata = responseEnvelope(verifyError.response)?.data || data;
			if (data.error === 'USER_NOT_FOUND' && metadata.registration_token) {
				setRegistrationToken(metadata.registration_token);
				setCode('');
				setState(OTP_STATES.CONFIRM_REGISTRATION);
				return;
			}
			if (['CHALLENGE_EXPIRED', 'CHALLENGE_LOCKED'].includes(data.error)) {
				clearProofs();
				setState(OTP_STATES.ENTER_CONTACT);
			}
			else setState(OTP_STATES.ENTER_CODE);
			setError(errorMessage(verifyError, 'Verification failed. Check the code and try again.'));
		}
	}, [challengeId, clearProofs, code, lastIdentifier, method, onAuthenticated]);

	const register = useCallback(async () => {
		setState(OTP_STATES.REGISTERING);
		setError('');
		try {
			const response = await userAPI.register(registrationToken);
			setState(OTP_STATES.AUTHENTICATED);
			clearProofs();
			try {
				await onAuthenticated(response, { method, identifier: lastIdentifier });
			} catch {
				setError('Your account was created, but this payment request is unavailable.');
				setState(OTP_STATES.ENTER_CONTACT);
			}
		} catch (registrationError) {
			const errorCode = registrationError.response?.data?.error;
			if (errorCode === 'REGISTRATION_TOKEN_EXPIRED' || errorCode === 'IDENTITY_ALREADY_IN_USE') {
				clearProofs();
				setState(OTP_STATES.ENTER_CONTACT);
			} else {
				setState(OTP_STATES.CONFIRM_REGISTRATION);
			}
			setError(errorMessage(registrationError, 'Account creation failed. Try again.'));
		}
	}, [clearProofs, lastIdentifier, method, onAuthenticated, registrationToken]);

	const changeMethod = useCallback((nextMethod) => {
		clearProofs();
		setError('');
		setCountdown(0);
		setLastIdentifier('');
		setMethod(nextMethod);
		setState(OTP_STATES.ENTER_CONTACT);
	}, [clearProofs]);

	const back = useCallback(() => {
		clearProofs();
		setError('');
		setState(OTP_STATES.ENTER_CONTACT);
	}, [clearProofs]);

	return {
		state, method, changeMethod, code, setCode, countdown, error, maskedDestination,
		sendCode, verify, register, resend: () => sendCode(lastIdentifier), back,
	};
}
